/**
 * Hand ordering. See scope.md §8.4.
 *
 * The player arranges their own hand by picking a card and nudging it one place at a
 * time. There is no dragging — it was fiddly on a phone (Ben, 16 September 2026) — and
 * no sort buttons: with a dozen cards in front of
 * you, "by suit" is rarely the arrangement you actually want, and shuffling them into
 * your own order is half the pleasure of holding cards.
 *
 * This is local display state only. It is never sent to the server and is never part
 * of game state — two players must never disagree about the game because one of them
 * arranged their cards differently.
 */

import { type Card, type MeldKind, type NaturalRank, isJoker, runOrder } from '@garbage/shared';

const SUIT_ORDER = ['spades', 'hearts', 'diamonds', 'clubs'] as const;

function suitIndex(card: Card): number {
  if (!card.suit) return -1; // jokers lead
  const i = (SUIT_ORDER as readonly string[]).indexOf(card.suit);
  return i === -1 ? SUIT_ORDER.length : i;
}

function order(card: Card): number {
  return isJoker(card) ? 0 : runOrder(card.rank as NaturalRank, false);
}

/**
 * How a freshly dealt hand is grouped. A round of nothing but sets starts with like
 * ranks together; every other round starts by suit, which is how runs are built.
 */
export type Grouping = 'suit' | 'rank';

/** Sets only → by rank. Runs only, or a mix → by suit. Ben's call, 16 September 2026. */
export function groupingFor(kinds: readonly MeldKind[]): Grouping {
  return kinds.length > 0 && kinds.every((kind) => kind === 'set') ? 'rank' : 'suit';
}

/**
 * The order a freshly dealt hand arrives in, jokers first: by suit rising in rank, or by
 * rank with the suits together. Only a starting point — from there the player arranges
 * it themselves.
 */
export function defaultOrder(hand: readonly Card[], grouping: Grouping = 'suit'): Card[] {
  return hand.slice().sort((a, b) => {
    if (isJoker(a) || isJoker(b)) return (isJoker(a) ? 0 : 1) - (isJoker(b) ? 0 : 1);
    const bySuit = suitIndex(a) - suitIndex(b);
    const byRank = order(a) - order(b);
    return grouping === 'rank' ? byRank || bySuit : bySuit || byRank;
  });
}

/**
 * Apply a player's arrangement to the live hand.
 *
 * The hand changes underneath the order constantly — cards are drawn, melded, claimed
 * — so the arrangement is kept as card ids and reconciled: known ids keep their place,
 * ids for cards no longer held are dropped, and new cards go at the **start**, on the
 * left, where the player will see what they have just picked up.
 */
export function applyCustomOrder(hand: readonly Card[], order_: readonly string[]): Card[] {
  const known = new Set(order_);
  const fresh = hand.filter((c) => !known.has(c.id));
  const byId = new Map(hand.map((c) => [c.id, c]));
  const kept: Card[] = [];
  for (const id of order_) {
    const card = byId.get(id);
    if (card) {
      kept.push(card);
      byId.delete(id);
    }
  }
  return [...fresh, ...kept];
}

/**
 * The arrangement to keep after the server sends a new hand. A hand with no arrangement
 * yet — or one left over from a hand that is entirely gone, as at a new deal — starts
 * from the dealt order. Otherwise new cards join on the left.
 */
export function reconcileOrder(
  hand: readonly Card[], previous: readonly string[] | null, grouping: Grouping = 'suit',
): string[] {
  const held = new Set(hand.map((c) => c.id));
  if (!previous || !previous.some((id) => held.has(id))) return defaultOrder(hand, grouping).map((c) => c.id);
  return applyCustomOrder(hand, previous).map((c) => c.id);
}

/** Move one card to a new position. */
export function moveCard(order_: readonly string[], from: number, to: number): string[] {
  if (from === to) return order_.slice();
  if (from < 0 || from >= order_.length) return order_.slice();
  const clamped = Math.max(0, Math.min(order_.length - 1, to));
  const next = order_.slice();
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return order_.slice();
  next.splice(clamped, 0, moved);
  return next;
}

/**
 * Nudge one card a single place left or right. This is how a hand is arranged.
 */
export function nudge(order_: readonly string[], cardId: string, delta: -1 | 1): string[] {
  const from = order_.indexOf(cardId);
  if (from === -1) return order_.slice();
  return moveCard(order_, from, from + delta);
}

/** The order shown to the player: their own arrangement, or the dealt order. */
export function displayHand(hand: readonly Card[], customOrder: readonly string[] | null): Card[] {
  if (customOrder && customOrder.length > 0) return applyCustomOrder(hand, customOrder);
  return defaultOrder(hand);
}
